import React from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import KPICard from '../../components/primitives/KPICard';
import Panel from '../../components/primitives/Panel';
import StatusPill from '../../components/primitives/StatusPill';
import { getBranch, ISO_EVENTS } from '../../utils/orgData';
import { PG } from '../../utils/theme';

const { FiCheckCircle, FiAlertTriangle, FiClock, FiLayers, FiFileText } = FiIcons;

const severityTone = (s) => (s === 'High' ? 'alert' : s === 'Medium' ? 'warn' : s === 'Low' ? 'info' : 'neutral');

const MOCK_CAPAS = [
  { id: 'CAPA-1127', title: 'Repeat rework — Ford Ranger windscreens', pct: 65, tone: 'warn' },
  { id: 'CAPA-1109', title: 'Torque calibration cadence tightened', pct: 90, tone: 'ok' },
  { id: 'CAPA-1142', title: 'Customer signature discipline on job cards', pct: 40, tone: 'alert' },
  { id: 'CAPA-1151', title: 'Supplier batch traceability', pct: 75, tone: 'warn' },
];

const AUDIT_HISTORY = [
  { date: '2026-04-02', type: 'Internal', result: 'Pass — 0 major', tone: 'ok' },
  { date: '2026-03-12', type: 'Insurer', result: 'Pass — 1 observation', tone: 'warn' },
  { date: '2026-02-28', type: 'OEM (Toyota)', result: 'Pass', tone: 'ok' },
  { date: '2026-02-05', type: 'Internal', result: 'Pass — 0 major', tone: 'ok' },
  { date: '2026-01-18', type: 'SABS surveillance', result: 'Pass', tone: 'ok' },
];

const DOC_LIST = [
  { code: 'WI-001', title: 'Windscreen removal', ver: 'v5.1', reviewer: 'T. van Wyk', ok: true },
  { code: 'WI-002', title: 'Primer application', ver: 'v4.3', reviewer: 'T. van Wyk', ok: true },
  { code: 'WI-003', title: 'Adhesive cure cycle', ver: 'v4.2', reviewer: 'T. van Wyk', ok: true },
  { code: 'WI-007', title: 'ADAS calibration', ver: 'v2.1', reviewer: 'S. Naidoo', ok: true },
  { code: 'WI-012', title: 'Customer handover', ver: 'v3.0', reviewer: 'B. Khumalo', ok: true },
  { code: 'QP-021', title: 'Internal audit programme', ver: 'v2.4', reviewer: 'T. van Wyk', ok: true },
  { code: 'QP-033', title: 'CAPA workflow', ver: 'v1.8', reviewer: 'T. van Wyk', ok: false },
  { code: 'SOP-044', title: 'Waste glass disposal', ver: 'v1.2', reviewer: 'B. Khumalo', ok: true },
  { code: 'SOP-051', title: 'PPE inspection daily', ver: 'v2.0', reviewer: 'Safety Rep', ok: true },
  { code: 'FM-073', title: 'Job card template', ver: 'v6.1', reviewer: 'Branch Mgr', ok: true },
];

export default function ISOBranchView() {
  const { branchId } = useParams();
  const branch = getBranch(branchId);

  if (!branch) {
    return <div className="h-full flex items-center justify-center text-neutral-500 font-mono-tech text-xs uppercase tracking-widest">Branch not found</div>;
  }

  const branchEvents = ISO_EVENTS.filter((e) => e.branch === branch.name || e.branch === 'Group');

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col gap-4 min-h-0"
    >
      <div className="flex items-baseline justify-between flex-shrink-0">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-neutral-500">ISO / Branch drilldown</div>
          <div className="flex items-baseline gap-3 mt-1">
            <h1 className="font-doto text-3xl text-white">{branch.name}</h1>
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">{branch.town} &middot; {branch.country}</span>
          </div>
        </div>
        <StatusPill tone={branch.iso.ncrOverdue > 0 ? 'alert' : 'ok'}>Peer: {branch.peerGroup}</StatusPill>
      </div>

      <div className="grid grid-cols-5 gap-3 flex-shrink-0">
        <KPICard title="COMPLIANCE" value={branch.iso.complianceScore} unit="%" icon={FiCheckCircle} accent={PG.emerald} target=">98%" />
        <KPICard title="NCR OPEN" value={branch.iso.ncrOpen} icon={FiAlertTriangle} accent={PG.amber} />
        <KPICard title="NCR OVERDUE" value={branch.iso.ncrOverdue} icon={FiClock} accent={branch.iso.ncrOverdue > 0 ? PG.red : PG.emerald} />
        <KPICard title="CAPA EFFECTIVENESS" value={branch.iso.capaEffectiveness} unit="%" icon={FiLayers} accent={PG.emerald} />
        <KPICard title="DOC CONTROL" value={branch.iso.docControlScore} unit="%" icon={FiFileText} accent={PG.cyan} />
      </div>

      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        <Panel title="BRANCH_NCR_LOG" subtitle={`${branchEvents.length} entries`} className="col-span-6">
          <div className="h-full overflow-y-auto custom-scrollbar">
            {branchEvents.length === 0 ? (
              <div className="h-full flex items-center justify-center font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">No open NCRs for this branch</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 bg-black/40 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2">ID</th>
                    <th className="text-left px-3 py-2">Title</th>
                    <th className="text-left px-3 py-2">Severity</th>
                    <th className="text-right px-3 py-2">Age</th>
                  </tr>
                </thead>
                <tbody>
                  {branchEvents.map((e) => (
                    <tr key={e.id} className="border-t border-white/5">
                      <td className="px-3 py-2 font-mono-tech text-[10px] text-neutral-400">{e.id}</td>
                      <td className="px-3 py-2 text-neutral-300">{e.title}</td>
                      <td className="px-3 py-2"><StatusPill tone={severityTone(e.severity)}>{e.severity}</StatusPill></td>
                      <td className="px-3 py-2 text-right font-mono-tech text-[10px] text-neutral-500">{e.age}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Panel>

        <Panel title="CAPA_STATUS" subtitle="active corrective actions" className="col-span-6">
          <div className="h-full overflow-y-auto custom-scrollbar p-3 space-y-3">
            {MOCK_CAPAS.map((c) => (
              <div key={c.id} className="border border-white/5 p-3 bg-black/40">
                <div className="flex justify-between items-baseline mb-2">
                  <div>
                    <span className="font-mono-tech text-[10px] text-neutral-400">{c.id}</span>
                    <div className="text-sm text-white mt-0.5">{c.title}</div>
                  </div>
                  <StatusPill tone={c.tone}>{c.pct}%</StatusPill>
                </div>
                <div className="w-full h-1.5 bg-white/5">
                  <div
                    className="h-full"
                    style={{
                      width: `${c.pct}%`,
                      backgroundColor: c.tone === 'ok' ? PG.emerald : c.tone === 'warn' ? PG.amber : PG.red,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="AUDIT_HISTORY_90D" subtitle="timeline" className="col-span-5">
          <div className="h-full overflow-y-auto custom-scrollbar">
            {AUDIT_HISTORY.map((a, i) => (
              <div key={i} className="flex items-start gap-3 px-4 py-3 border-b border-white/5">
                <div className="w-2 h-2 mt-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: a.tone === 'ok' ? PG.emerald : PG.amber }} />
                <div className="flex-1">
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">{a.date}</div>
                  <div className="text-white text-sm">{a.type}</div>
                  <div className="text-xs text-neutral-400">{a.result}</div>
                </div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="DOC_CONTROL_CHECKLIST" subtitle="procedures current" className="col-span-7">
          <div className="h-full overflow-y-auto custom-scrollbar">
            <table className="w-full text-xs">
              <thead className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 bg-black/40 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">Code</th>
                  <th className="text-left px-3 py-2">Title</th>
                  <th className="text-left px-3 py-2">Version</th>
                  <th className="text-left px-3 py-2">Reviewer</th>
                  <th className="text-center px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {DOC_LIST.map((d) => (
                  <tr key={d.code} className="border-t border-white/5">
                    <td className="px-3 py-2 font-mono-tech text-[10px] text-neutral-400">{d.code}</td>
                    <td className="px-3 py-2 text-neutral-300">{d.title}</td>
                    <td className="px-3 py-2 font-mono-tech text-[10px] text-neutral-500">{d.ver}</td>
                    <td className="px-3 py-2 text-neutral-400 text-xs">{d.reviewer}</td>
                    <td className="px-3 py-2 text-center">
                      <StatusPill tone={d.ok ? 'ok' : 'warn'}>{d.ok ? 'Current' : 'Review Due'}</StatusPill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </motion.div>
  );
}
